import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import { Search, X, Mail, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import "./EmailArchive.css";

const PAGE_SIZE = 25;

const TYPE_OPTIONS = [
  { value: "composer",        label: "Composer" },
  { value: "vendor_accepted", label: "Acceptance" },
  { value: "payment",         label: "Payment" },
  { value: "welcome",         label: "Welcome" },
  { value: "auth",            label: "Auth / system" },
  { value: "other",           label: "Other" },
];

const STATUS_META = {
  delivered:        { label: "Delivered", tone: "green",  Icon: CheckCircle2 },
  sent:             { label: "Sent",      tone: "gray",   Icon: Mail },
  queued:           { label: "Queued",    tone: "gray",   Icon: Clock },
  delivery_delayed: { label: "Delayed",   tone: "amber",  Icon: Clock },
  bounced:          { label: "Bounced",   tone: "red",    Icon: AlertTriangle },
  complained:       { label: "Complaint", tone: "red",    Icon: AlertTriangle },
};

export default function EmailArchive() {
  const [messages, setMessages]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [page, setPage]           = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // ── Filters ──
  const [recipientQuery, setRecipientQuery] = useState("");
  const [textQuery, setTextQuery]           = useState("");
  const [dateFrom, setDateFrom]             = useState("");
  const [dateTo, setDateTo]                 = useState("");
  const [statusFilter, setStatusFilter]     = useState("");
  const [typeFilter, setTypeFilter]         = useState("");

  // Debounced copies — so typing doesn't fire a query per keystroke
  const [debouncedRecipient, setDebouncedRecipient] = useState("");
  const [debouncedText, setDebouncedText]           = useState("");

  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedRecipient(recipientQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [recipientQuery]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedText(textQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [textQuery]);

  // Reset to first page whenever the filter set changes — otherwise you
  // can end up stranded on page 5 of a 2-page result set.
  useEffect(() => {
    setPage(0);
  }, [debouncedRecipient, debouncedText, dateFrom, dateTo, statusFilter, typeFilter]);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setError("");

    // Only the row list is paginated; count comes back with it so the
    // archive never has to load everything at once. This table grows
    // forever by design, so unbounded selects aren't an option.
    let query = supabase
      .from("email_messages")
      .select("id, subject, to_email, from_email, cc_emails, email_type, status, created_at, delivered_at, bounced_at, opened_at, vendor_id, event_id", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

    if (debouncedRecipient) query = query.ilike("to_email", `%${debouncedRecipient}%`);
    if (debouncedText)      query = query.textSearch("search_vector", debouncedText, { type: "websearch", config: "english" });
    if (dateFrom)           query = query.gte("created_at", new Date(dateFrom).toISOString());
    if (dateTo) {
      // Inclusive of the whole end day, not just 00:00 on it.
      const end = new Date(dateTo);
      end.setHours(23, 59, 59, 999);
      query = query.lte("created_at", end.toISOString());
    }
    if (statusFilter) query = query.eq("status", statusFilter);
    if (typeFilter)   query = query.eq("email_type", typeFilter);

    const { data, error: qErr, count } = await query;

    if (qErr) {
      console.error("Email archive query failed:", qErr);
      setError(qErr.message);
      setMessages([]);
    } else {
      setMessages(data || []);
      setTotalCount(count ?? 0);
    }
    setLoading(false);
  }, [page, debouncedRecipient, debouncedText, dateFrom, dateTo, statusFilter, typeFilter]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Bodies can be large, so they're fetched only when a message is opened
  // rather than loaded with every list row.
  const openMessage = async (msg) => {
    setSelected({ ...msg, body_html: null, loadingBody: true });
    const { data, error: bodyErr } = await supabase
      .from("email_messages")
      .select("body_html")
      .eq("id", msg.id)
      .single();
    setSelected({
      ...msg,
      body_html: bodyErr ? null : data?.body_html,
      loadingBody: false,
      bodyError: bodyErr ? bodyErr.message : null,
    });
  };

  const clearFilters = () => {
    setRecipientQuery("");
    setTextQuery("");
    setDateFrom("");
    setDateTo("");
    setStatusFilter("");
    setTypeFilter("");
  };

  const hasFilters = recipientQuery || textQuery || dateFrom || dateTo || statusFilter || typeFilter;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className="ea-wrap">
      {/* ── Filter bar ── */}
      <div className="ea-filters">
        <div className="ea-search-field">
          <Search size={14} />
          <input
            className="ea-input"
            placeholder="Recipient email…"
            value={recipientQuery}
            onChange={(e) => setRecipientQuery(e.target.value)}
          />
        </div>

        <div className="ea-search-field">
          <Search size={14} />
          <input
            className="ea-input"
            placeholder="Search subject & body…"
            value={textQuery}
            onChange={(e) => setTextQuery(e.target.value)}
          />
        </div>

        <input
          type="date"
          className="ea-input ea-input--date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          aria-label="From date"
        />
        <input
          type="date"
          className="ea-input ea-input--date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          aria-label="To date"
        />

        <select className="ea-input ea-input--select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Any status</option>
          {Object.entries(STATUS_META).map(([val, meta]) => (
            <option key={val} value={val}>{meta.label}</option>
          ))}
        </select>

        <select className="ea-input ea-input--select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">Any type</option>
          {TYPE_OPTIONS.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        {hasFilters && (
          <button type="button" className="ea-clear-btn" onClick={clearFilters}>
            <X size={13} /> Clear
          </button>
        )}
      </div>

      <div className="ea-result-meta">
        {loading ? "Searching…" : `${totalCount.toLocaleString()} email${totalCount === 1 ? "" : "s"}`}
      </div>

      {error && <p className="ea-error">{error}</p>}

      {/* ── Message list ── */}
      {!loading && messages.length === 0 ? (
        <p className="ea-empty">
          {hasFilters ? "No emails match these filters." : "No emails archived yet."}
        </p>
      ) : (
        <div className="ea-list">
          {messages.map((msg) => {
            const meta = STATUS_META[msg.status] || STATUS_META.sent;
            const Icon = meta.Icon;
            return (
              <button
                type="button"
                key={msg.id}
                className="ea-row"
                onClick={() => openMessage(msg)}
              >
                <span className={`ea-status ea-status--${meta.tone}`} title={meta.label}>
                  <Icon size={14} />
                </span>
                <span className="ea-row-to">{msg.to_email}</span>
                <span className="ea-row-subject">{msg.subject || <em>(no subject)</em>}</span>
                <span className="ea-row-type">{TYPE_OPTIONS.find((t) => t.value === msg.email_type)?.label || msg.email_type}</span>
                <span className="ea-row-date">{new Date(msg.created_at).toLocaleString()}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalCount > PAGE_SIZE && (
        <div className="ea-pagination">
          <button
            type="button"
            className="ea-page-btn"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0 || loading}
          >
            ← Previous
          </button>
          <span className="ea-page-info">Page {page + 1} of {totalPages}</span>
          <button
            type="button"
            className="ea-page-btn"
            onClick={() => setPage((p) => p + 1)}
            disabled={page + 1 >= totalPages || loading}
          >
            Next →
          </button>
        </div>
      )}

      {/* ── Detail pane ── */}
      {selected && (
        <div className="ea-modal-backdrop" onClick={() => setSelected(null)}>
          <div className="ea-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ea-modal-header">
              <h3>{selected.subject || "(no subject)"}</h3>
              <button type="button" className="ea-modal-x" onClick={() => setSelected(null)}>
                <X size={16} />
              </button>
            </div>

            <div className="ea-detail-meta">
              <div><span>To</span><strong>{selected.to_email}</strong></div>
              {selected.from_email && <div><span>From</span><strong>{selected.from_email}</strong></div>}
              {selected.cc_emails?.length > 0 && (
                <div><span>Cc</span><strong>{selected.cc_emails.join(", ")}</strong></div>
              )}
              <div><span>Sent</span><strong>{new Date(selected.created_at).toLocaleString()}</strong></div>
              <div><span>Status</span><strong>{(STATUS_META[selected.status] || {}).label || selected.status}</strong></div>
              {selected.delivered_at && <div><span>Delivered</span><strong>{new Date(selected.delivered_at).toLocaleString()}</strong></div>}
              {selected.bounced_at && <div><span>Bounced</span><strong>{new Date(selected.bounced_at).toLocaleString()}</strong></div>}
              {selected.opened_at && <div><span>Opened</span><strong>{new Date(selected.opened_at).toLocaleString()}</strong></div>}
            </div>

            <div className="ea-detail-body">
              {selected.loadingBody ? (
                <p className="ea-muted">Loading message…</p>
              ) : selected.body_html ? (
                <div dangerouslySetInnerHTML={{ __html: selected.body_html }} />
              ) : (
                <p className="ea-muted">
                  No stored copy of this message body. This email was sent outside the app
                  (most likely a system or auth email), so only its delivery record was captured.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}